import { ClientHttpRequester } from '@smarterservices/client-http-requester';
import config, { API_PATHS } from '../config';
import useAppStore from '../stores/app';
import useSessionStore from '../stores/session';

let apiClient;

const getApiClient = () => {
  if (!apiClient) {
    // Build our requester with default configs
    const requesterConfig = {
      apiUrl: config.proctoring.api.baseUrl,
      retry: {
        enabled: config.proctoring.api.retry,
      },
    };

    apiClient = ClientHttpRequester(requesterConfig);
  }
  return apiClient;
};

const buildHeaders = (headers = {}) => {
  const authHeader = { token: useAppStore.getState().user.token };
  return { ...headers, ...authHeader };
};

export const buildPath = (path, params) => {
  return path.replace(/:([^/]+)/g, (_, key) => {
    return key in params ? params[key] : `:${key}`;
  });
};

export async function checkAuthentication(token) {
  const pathReplacements = {};

  const url = buildPath(API_PATHS.EXTEND_TTL, pathReplacements);

  try {
    const response = await getApiClient().get(url, { token });
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e.response.status, error: e };
  }
}

export async function login(username, password) {
  const pathReplacements = {};

  const url = buildPath(API_PATHS.LOGIN, pathReplacements);

  const payload = {
    username,
    password,
  };

  try {
    const response = await getApiClient().post(url, {}, payload);
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e?.response?.status, error: e };
  }
}

export async function getIamPermissions() {
  const pathReplacements = {};

  const url = buildPath(API_PATHS.TOKEN_PERMISSIONS, pathReplacements);

  try {
    const response = await getApiClient().get(url, buildHeaders());
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e?.response?.status, error: e };
  }
}

export async function getRooms() {
  const pathReplacements = {};

  const url = buildPath(API_PATHS.LIST_ROOMS, pathReplacements);

  try {
    const response = await getApiClient().get(url, buildHeaders());

    // if the response is successful, we need to limit the results to only rooms that are v3. Also filter the locations poperly as well.

    // Step 1: Filter rooms to only include those with version >= 3.

    const filteredRooms = response.data.rooms.filter(
      (room) => room?.version >= 3
    );

    //const filteredRooms = response.data.rooms;

    // Step 2: For each location, attach a 'rooms' array with rooms matching that location's SID.
    const resultLocations = response.data.locations
      .map((location) => {
        const associatedRooms = filteredRooms
          .filter(
            (room) =>
              room.proctorAccountLocationSid ===
              location.proctorAccountLocationSid
          )
          .sort((a, b) => a.roomName.localeCompare(b.roomName));
        const activeCount = associatedRooms.filter(
          (room) => room.isActive
        ).length;
        return { ...location, rooms: associatedRooms, activeCount };
      })
      // Only include locations that have at least one matching room.
      .filter((location) => location.rooms.length > 0);

    // Final result.
    const result = { locations: resultLocations, rooms: filteredRooms };

    return { successful: true, data: result };
  } catch (e) {
    return { successful: false, statusCode: e?.response?.status, error: e };
  }
}

export async function getRoom(roomSid) {
  const pathReplacements = { roomSid };

  const url = buildPath(API_PATHS.ROOMS, pathReplacements);

  try {
    const response = await getApiClient().get(url, buildHeaders());
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e.response.status, error: e };
  }
}

export async function joinRoom(proctorAccountSid, proctorAccountLocationSid, roomSid) {
  const pathReplacements = {
    proctorAccountSid,
    proctorAccountLocationSid,
    roomSid
  };

  const url = buildPath(API_PATHS.JOIN_ROOM, pathReplacements);

  try {
    const response = await getApiClient().put(url, buildHeaders());
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e?.response?.status, error: e.response.data };
  }
}
export async function exitRoom(proctorAccountSid, proctorAccountLocationSid, roomSid) {
  const pathReplacements = {
    proctorAccountSid,
    proctorAccountLocationSid,
    roomSid
  };

  const url = buildPath(API_PATHS.EXIT_ROOM, pathReplacements);

  try {
    const response = await getApiClient().delete(url, buildHeaders());
    return { successful: true, data: response.data };
  } catch (e) {
    return { successful: false, statusCode: e?.response?.status, error: e.response.data  };
  }
}

export async function getAvailableRoomsForTransfer(sessionSid) {
  const sessionData = useSessionStore.getState().getSessionData(sessionSid);

  if (sessionData) {
    const pathReplacements = {
      roomSid: useAppStore.getState().roomSid,
      installSid: sessionData.installSid,
      examSid: sessionData.examSid,
      sessionSid: sessionSid,
    };

    const url = buildPath(API_PATHS.ROOMS_FOR_TRANSFER, pathReplacements);

    try {
      const response = await getApiClient().get(url, buildHeaders());
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, error: e };
    }
  } else {
    // TODO: throw some error.
  }
}

export async function transferSession({sessionSid, fromRoomSid, toRoomSid, transferredBySid, note}) {
  const sessionData = useSessionStore.getState().getSessionData(sessionSid);

  if (sessionData) {
    const pathReplacements = {
      installSid: sessionData.installSid,
      examSid: sessionData.examSid,
      sessionSid: sessionSid,
    };
    const payload = {
      fromProctorRoomSid: fromRoomSid,
      toProctorRoomSid: toRoomSid,
      transferredBySid,
      note: note,
    };

    const url = buildPath(API_PATHS.TRANSFER_SESSION, pathReplacements);

    try {
      const response = await getApiClient().patch(url, buildHeaders(), payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, error: e };
    }
  } else {
    // TODO: throw some error.
  }
}

export async function setSessionAsCheckedIn({sessionSid}) {
  const sessionData = useSessionStore.getState().getSessionData(sessionSid);

  if (sessionData) {
    const pathReplacements = {
      installSid: sessionData.installSid,
      examSid: sessionData.examSid,
      sessionSid: sessionSid,
    };
    const payload = {
      status: "Checked In",
    };

    const url = buildPath(API_PATHS.TRANSFER_SESSION, pathReplacements);

    try {
      const response = await getApiClient().patch(url, buildHeaders(), payload);
      return { successful: true, data: response.data };
    } catch (e) {
      return { successful: false, error: e };
    }
  } else {
    // TODO: throw some error.
  }
}

export { API_PATHS };
export { buildHeaders };
export let api = getApiClient();
